import { ArrowCounterClockwise, Check, CheckCircle } from '@phosphor-icons/react';
import type { PersonColor } from '@shh/design-tokens';
import type { ReactNode } from 'react';
import { Avatar } from '../atoms/Avatar';
import { Button } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import { cardSurface } from '../lib/card';
import { cn } from '../lib/cn';
import { PointsPill } from './PointsPill';
import { uiText } from '../i18n';

export interface ApprovalItemProps {
  name: string;
  color: PersonColor;
  emoji?: string;
  /** What is waiting, e.g. "Zimmer aufgeräumt" or "möchte: Kinoabend". */
  what: ReactNode;
  points: number;
  /** Points are earned (task) or spent (reward). */
  kind?: 'earn' | 'spend';
  state?: 'waiting' | 'approved' | 'returned';
  onApprove?: () => void;
  onReturn?: () => void;
  returnLabel?: string;
  className?: string;
}

/** One thing waiting for a parent: two large, clearly different actions. */
export function ApprovalItem({
  name,
  color,
  emoji,
  what,
  points,
  kind = 'earn',
  state = 'waiting',
  onApprove,
  onReturn,
  returnLabel,
  className,
}: ApprovalItemProps) {
  return (
    <div className={cn('flex flex-col gap-gap-md rounded-card p-inset-sm', cardSurface, className)}>
      <div className="flex items-center gap-gap-md">
        <Avatar name={name} color={color} emoji={emoji} size="sm" decorative />
        <p className="min-w-0 flex-1 text-md">
          <strong>{name}</strong> · {what}
        </p>
        <PointsPill points={kind === 'spend' ? -points : points} signed={kind === 'earn'} />
      </div>
      {state === 'waiting' ? (
        <div className="grid grid-cols-2 gap-gap-sm">
          <Button variant="secondary" icon={ArrowCounterClockwise} onPress={onReturn}>
            {returnLabel ?? uiText().again}
          </Button>
          <Button icon={Check} onPress={onApprove}>
            Freigeben
          </Button>
        </div>
      ) : (
        <p
          role="status"
          className="flex items-center gap-gap-xs text-sm font-semibold text-feedback-success"
        >
          <Icon icon={CheckCircle} size="sm" weight="fill" />
          {state === 'approved'
            ? kind === 'earn'
              ? uiText().approvedEarn(name, uiText().stars(points))
              : uiText().approvedRedeem(uiText().stars(points))
            : uiText().returned}
        </p>
      )}
    </div>
  );
}
